import Link from '@components/Link';
import Tooltip from '@components/tooltip';
import useMediaQueries from '@hooks/useMediaQueries';
import Menu from '@retracedgmbh/ui-components-lib/Menu';
import UIMenuItem from '@retracedgmbh/ui-components-lib/MenuItem';
import SubMenu from '@retracedgmbh/ui-components-lib/SubMenu';
import { useAuth } from '@state/auth';
import { useNavbar } from '@state/navbar';
import classNames from 'classnames';
import React from 'react';
import { useLocation } from 'react-router-dom';
import { useCanAccessKharonV2 } from '@/hooks/useCanAccessKharonV2';
import useEnabledDevelopmentTags from '@/hooks/useEnabledDevelopmentTags';
import useFeature from '@/hooks/useFeature';
import useTranslator from '@/hooks/useTranslator';
import { COMPANYLESS_LANDING_PATH, isCompanyless } from '@/lib/companyless';
import SideBarAccount from './sidebar-accounts';

const CompanylessSidebar = ({
  isNavbarCollapsed,
  toggleNavbar,
  accountProps,
  className,
}) => {
  const { getTranslation } = useTranslator({ page: 'sidebar' });

  const companylessMenu = [
    {
      name: getTranslation('sidebar.companylessMenu.searchCompany'),
      icon: 'fa-light fa-magnifying-glass',
      path: COMPANYLESS_LANDING_PATH,
      highlightRegex: new RegExp(`^${COMPANYLESS_LANDING_PATH}`),
    },
  ];

  return (
    <div
      id='sideMenu'
      className={`sidebar ${isNavbarCollapsed ? 'is-collapsed' : 'is-expanded'} ${className}`}
      style={{ borderRightWidth: 1, boxShadow: 'none' }}
    >
      <div className='sidebar-body'>
        <Menu isCollapsed={isNavbarCollapsed}>
          {companylessMenu.map(menu => (
            <MenuItem
              key={menu.name}
              {...menu}
              title={menu.name}
              highlightRegex={menu.highlightRegex}
              onClick={menu.onClick}
            />
          ))}
          <hr />
          <SideBarAccount {...accountProps} />
        </Menu>
      </div>

      <button
        type='button'
        className='no-styled-button sidebar-footer is-hidden-mobile'
        onClick={toggleNavbar}
      >
        <span className='icon is-medium '>
          <i
            className={classNames('fa fa-light', {
              'fa-angle-double-right': isNavbarCollapsed,
              'fa-angle-double-left': !isNavbarCollapsed,
            })}
          />
        </span>
      </button>
    </div>
  );
};

const DashboardSidebar = ({
  isNavbarCollapsed,
  toggleNavbar,
  accountProps,
  className,
}) => {
  const { isCapaV2Enabled, isFeatureEnabled } = useFeature();
  useEnabledDevelopmentTags();

  const me = useAuth(state => state.me);
  const canAccessKharonV2 = useCanAccessKharonV2();
  const screens = useMediaQueries();
  const isSideBarCollapsed = isNavbarCollapsed && screens.md;
  const { getTranslation } = useTranslator({ page: 'sidebar' });

  const hasStyles = me?.company?.facilityProcesses
    ?.split(',')
    ?.map(process => process.trim())
    ?.some(process =>
      [
        'BRAND',
        'RETAILER',
        'GARMENT_FINISHING',
        'CUT_MAKE_TRIM',
        'SOURCING_AGENCY',
        'WHOLESALER',
      ].includes(process),
    );

  const [menu, setMenu] = React.useState([]);
  const isProductChainEnabled = isFeatureEnabled('product-mapping');
  const isCascadeMappingEnabled = isFeatureEnabled('supply-chain-mapping');

  React.useEffect(() => {
    const newMenu = [
      {
        name: 'sidebar.dashboardMenu.dashboard',
        id: 'dashboard',
        icon: 'fa-light fa-house',
        path: '/',
        highlightRegex: /^\/$/,
      },
      {
        name: 'sidebar.dashboardMenu.network',
        id: 'network',
        icon: 'fa-light fa-building',
        path: '/companies/list',
        highlightRegex: /^\/(companies|users)/,
      },
      {
        name: 'sidebar.dashboardMenu.products',
        id: 'products',
        submenus: [
          {
            name: hasStyles
              ? 'sidebar.dashboardMenu.productSubMenu.styles'
              : 'sidebar.dashboardMenu.productSubMenu.myProducts',
            icon: 'fa-light fa-tshirt',
            path: '/my-styles',
            highlightRegex: /^\/my-styles(\/|$)/,
          },
          {
            name: 'sidebar.dashboardMenu.productSubMenu.myComponents',
            icon: 'fa-light fa-puzzle',
            path: '/my-components',
            highlightRegex: /^\/my-components(\/|$)/,
          },
        ],
        icon: 'fa-light fa-clothes-hanger',
        path: '/my-styles',
        highlightRegex: /^\/my-styles(\/|$)/,
      },

      {
        name: 'sidebar.dashboardMenu.vendorCompliance',
        id: 'vendorCompliance',
        icon: 'fa-light fa-ballot-check',
        submenus: [
          {
            name: 'sidebar.dashboardMenu.vendorComplianceSubMenu.documents',
            icon: 'fa-light fa-file',
            path: '/company-documents/list',
            highlightRegex: /^\/company-documents/,
          },
          {
            name: 'sidebar.dashboardMenu.vendorComplianceSubMenu.certifications',
            icon: 'fa-light fa-award',
            path: '/claims/list?isCertification=true&sort=expirationDate:ASC',
            highlightRegex: /^\/claims/,
          },
          {
            name: 'sidebar.dashboardMenu.vendorComplianceSubMenu.audits',
            icon: 'fa-light fa-clipboard-list',
            path: '/audits',
            highlightRegex: /^\/audits/,
          },
          {
            name: 'sidebar.dashboardMenu.vendorComplianceSubMenu.slcp',
            icon: 'fa-light fa-list',
            path: '/slcp/assessments',
            highlightRegex: /^\/slcp/,
          },
        ],
      },
      {
        name: 'sidebar.dashboardMenu.traceability',
        id: 'traceability',
        icon: 'fa-light fa-route',
        submenus: [],
      },
    ];

    isFeatureEnabled('risk-analysis-v3') &&
      newMenu.push({
        name: 'sidebar.dashboardMenu.riskManagement',
        id: 'riskManagement',
        icon: 'fa-light fa-mountains',
        submenus: [
          {
            name: 'sidebar.dashboardMenu.riskManagement.riskLandscape',
            icon: 'fa-light fa-mountains',
            path: '/risk-landscape/view',
            highlightRegex: /^\/risk-landscape/,
          },
          {
            name: 'sidebar.dashboardMenu.riskManagement.riskRules',
            icon: 'fa-light fa-tachometer',
            path: '/risk-rules',
            highlightRegex: /^\/risk-rules/,
          },
          {
            name: 'sidebar.dashboardMenu.riskManagement.riskAssessments',
            icon: 'fa-light fa-tachometer',
            path: '/risk-assessments',
            highlightRegex: /^\/risk-assessments/,
          },
        ],
      });

    newMenu
      .find(menu => menu.id === 'vendorCompliance')
      .submenus.splice(0, 0, {
        name: 'sidebar.dashboardMenu.questionnaires',
        icon: 'fa-light fa-tachometer',
        path: '/questionnaires',
        id: 'questionnaires',
        highlightRegex: /^\/questionnaires/,
      });

    isCapaV2Enabled &&
      newMenu
        .find(menu => menu.id === 'vendorCompliance')
        .submenus.splice(3, 0, {
          name: 'sidebar.dashboardMenu.vendorComplianceSubMenu.capaV2',
          icon: 'fa-light fa-table',
          path: '/capa-v2',
          highlightRegex: /^\/capa-v2/,
        });

    isCascadeMappingEnabled &&
      newMenu
        .find(menu => menu.id === 'traceability')
        .submenus.push({
          name: 'sidebar.dashboardMenu.traceabilitySubMenu.supplyChainMapping',
          icon: 'fa-light fa-share-alt',
          path: '/company-chains',
          highlightRegex: /^\/company-chains/,
        });

    isProductChainEnabled &&
      newMenu
        .find(menu => menu.id === 'traceability')
        .submenus.push({
          name: 'sidebar.dashboardMenu.traceabilitySubMenu.productMapping',
          icon: 'fa-light fa-tshirt',
          path: '/collaborative-product-mapping/list',
          highlightRegex: /^\/collaborative-product-mapping/,
        });

    isFeatureEnabled('cascade-tracing') &&
      newMenu
        .find(menu => menu.id === 'traceability')
        .submenus.push({
          name: 'sidebar.dashboardMenu.traceabilitySubMenu.orders',
          icon: 'fa-light fa-box-check',
          path: '/cascade-tracing?tab=sales-orders',
          highlightRegex: /^\/cascade-tracing\b(?!\/file-center?)/,
        });

    const canAccessEntityScreeningV2 =
      isFeatureEnabled('entity-screening-v2') && canAccessKharonV2;

    canAccessEntityScreeningV2 &&
      newMenu
        .find(menu => menu.id === 'traceability')
        .submenus.push({
          name: 'sidebar.dashboardMenu.traceabilitySubMenu.entityScreeningV2',
          icon: 'fa-light fa-search',
          path: '/entity-screening-v2',
          highlightRegex: /^\/entity-screening-v2/,
        });

    isFeatureEnabled('transaction-certificate-management') &&
      newMenu
        .find(menu => menu.id === 'traceability')
        .submenus.push({
          name: 'sidebar.dashboardMenu.traceabilitySubMenu.transactionCertificateManagement',
          icon: 'fa-light fa-certificate',
          path: '/transaction-certificate-management',
          highlightRegex: /^\/transaction-certificate-management/,
        });

    isFeatureEnabled('certified-materials-brand') &&
      newMenu
        .find(menu => menu.id === 'traceability')
        .submenus.push({
          name: 'sidebar.dashboardMenu.traceabilitySubMenu.certifiedMaterialsManagement',
          icon: 'fa-light fa-file-certificate',
          path: '/certified-materials-management',
          highlightRegex: /^\/certified-materials-management/,
        });

    isFeatureEnabled('certified-materials-supplier') &&
      newMenu
        .find(menu => menu.id === 'traceability')
        .submenus.push({
          name: 'sidebar.dashboardMenu.traceabilitySubMenu.certifiedMaterialsManagement',
          icon: 'fa-light fa-file-certificate',
          path: '/certified-materials-management',
          highlightRegex: /^\/certified-materials-management/,
        });

    isFeatureEnabled('tracing-document-management') &&
      isFeatureEnabled('cascade-tracing') &&
      newMenu
        .find(menu => menu.id === 'traceability')
        .submenus.push({
          name: 'Files management',
          icon: 'fa-light fa-file',
          path: '/cascade-tracing/file-center',
          highlightRegex: /^\/cascade-tracing\/file-center/,
        });

    isFeatureEnabled('fiber-forward') &&
      newMenu
        .find(menu => menu.id === 'traceability')
        .submenus.push(
          {
            name: 'sidebar.dashboardMenu.traceabilitySubMenu.transactions',
            icon: 'fa-light fa-truck',
            path: '/fiber/transactions',
            highlightRegex: /^\/fiber\/transactions/,
          },
          {
            name: 'sidebar.dashboardMenu.traceabilitySubMenu.productions',
            icon: 'fa-light fa-industry',
            path: '/fiber/transformations',
            highlightRegex: /^\/fiber\/transformations/,
          },
          {
            name: 'sidebar.dashboardMenu.traceabilitySubMenu.inventory',
            icon: 'fa-light fa-boxes',
            path: '/fiber/inventory',
            highlightRegex: /^\/fiber\/inventory/,
          },
        );

    if (
      isFeatureEnabled('constants-mapping') ||
      isFeatureEnabled('self-service-api-key')
    ) {
      const submenus = [];

      const constantMappingMenu = {
        name: 'sidebar.dashboardMenu.developersHQSubMenu.constantsMapping',
        id: 'constantsMapping',
        icon: 'fa-light fa-square-code',
        path: '/constants-mapping',
        highlightRegex: /^\/constants-mapping/,
      };

      const selfServiceMenu = {
        id: 'apiKeys',
        name: 'sidebar.dashboardMenu.developersHQSubMenu.apiKeys',
        icon: 'fa-light fa-key-skeleton',
        path: '/api-keys',
        highlightRegex: /^\/api-keys/,
      };

      if (isFeatureEnabled('self-service-api-key')) {
        submenus.push(selfServiceMenu);
      }

      if (isFeatureEnabled('constants-mapping')) {
        submenus.push(constantMappingMenu);
      }

      newMenu.push({
        name: 'sidebar.dashboardMenu.developersHQ',
        id: 'constantsMapping',
        icon: 'fa-light fa-square-code',
        submenus,
      });
    }

    const reportSubMenus = [];
    if (isFeatureEnabled('cascade-tracing')) {
      reportSubMenus.push({
        name: 'sidebar.dashboardMenu.tracingOperations',
        icon: 'fa-light fa-chart-line',
        path: '/reports/analytics/tracing-operations',
        highlightRegex: /^\/reports\/analytics\/tracing-operations/,
      });

      reportSubMenus.push({
        name: 'sidebar.dashboardMenu.tracingCompliance',
        icon: 'fa-light fa-chart-line',
        path: '/reports/analytics/tracing-compliance',
        highlightRegex: /^\/reports\/analytics\/tracing-compliance/,
      });
    }

    reportSubMenus.push({
      name: 'sidebar.dashboardMenu.certificateCompliance',
      icon: 'fa-light fa-chart-line',
      path: '/reports/analytics/certificate-compliance',
      highlightRegex: /^\/reports\/analytics\/certificate-compliance/,
    });

    reportSubMenus.push({
      name: 'sidebar.dashboardMenu.auditCompliance',
      icon: 'fa-light fa-chart-line',
      path: '/reports/analytics/audit-compliance',
      highlightRegex: /^\/reports\/analytics\/audit-compliance/,
    });

    newMenu.push({
      name: 'sidebar.dashboardMenu.reports',
      id: 'reports',
      icon: 'fa-light fa-chart-line',
      submenus: reportSubMenus,
    });

    newMenu.push({
      name: 'sidebar.dashboardMenu.helpCenter',
      id: 'helpCenter',
      icon: 'fa-light fa-book',
      highlightRegex: /^\/standards/,
      onClick: () => window.open('https://help.retraced.com/', '_blank'),
    });

    newMenu.push({
      name: 'HRDD Academy',
      id: 'hrrdAcademy',
      icon: 'fa-light fa-graduation-cap',
      className: 'hrrd-academy-menu-item',
      highlightRegex: '',
      onClick: () => window.open('https://www.hrddacademy.com/', '_blank'),
    });

    setMenu(newMenu);

    return () => {};
  }, [me, canAccessKharonV2]);

  const menuState = localStorage.getItem('menuOpen');

  const [menuOpen, setOpenMenu] = React.useState({
    products: menuState?.products || false,
    vendorCompliance: menuState?.vendorCompliance || false,
    traceability: menuState?.traceability || false,
  });

  const handleSubMenuOpenStateChanged = (state, menu) => {
    setOpenMenu({ ...menuOpen, [menu]: state });
    localStorage.setItem(
      'menuOpen',
      JSON.stringify({ ...menuOpen, [menu]: state }),
    );
  };

  React.useEffect(() => {
    const menuState = localStorage.getItem('menuOpen');
    if (menuState) {
      setOpenMenu(JSON.parse(menuState));
    }
  }, []);

  return (
    <div
      id='sideMenu'
      className={`sidebar ${isSideBarCollapsed ? 'is-collapsed' : 'is-expanded'} ${className}`}
      style={{ borderRightWidth: 1, boxShadow: 'none' }}
    >
      <div className='sidebar-body' style={{ marginTop: 0 }}>
        <Menu
          isCollapsed={isSideBarCollapsed}
          inline={!isSideBarCollapsed}
          className='menu-item-container'
        >
          {menu.map(menu => {
            const menuId = menu.id;
            return menu.submenus ? (
              <SubMenu
                defaultState={menuOpen[menuId]}
                onStateChanged={state =>
                  handleSubMenuOpenStateChanged(state, menuId)
                }
                key={menu.name}
                title={getTranslation(menu.name)}
                icon={menu.icon}
                active={
                  menu.submenus.some(submenu =>
                    submenu.highlightRegex.test(location.pathname),
                  ) && isSideBarCollapsed
                }
                style={{
                  justifyContent: !isSideBarCollapsed ? 'flex-start' : 'center',
                }}
                rightComponent={
                  menu.rightComponent ? menu.rightComponent : null
                }
              >
                {menu.submenus.map((submenu, index) => {
                  return (
                    <MenuItem
                      {...submenu}
                      key={submenu.name + index}
                      title={submenu.name}
                      highlightRegex={submenu.highlightRegex}
                    />
                  );
                })}
              </SubMenu>
            ) : (
              <MenuItem
                {...menu}
                key={menu.name}
                title={menu.name}
                highlightRegex={menu.highlightRegex}
                onClick={menu.onClick}
              />
            );
          })}
          <SideBarAccount {...accountProps} />
        </Menu>
      </div>

      <button
        type='button'
        className='no-styled-button sidebar-footer is-hidden-mobile'
        onClick={toggleNavbar}
      >
        <span className='icon is-medium '>
          <i
            className={classNames('fa fa-light tw:text-2xl', {
              'fa-angle-double-right': isNavbarCollapsed,
              'fa-angle-double-left': !isNavbarCollapsed,
            })}
          />
        </span>
      </button>
    </div>
  );
};

const ManagementSidebar = ({
  isNavbarCollapsed,
  toggleNavbar,
  me,
  accountProps,
  className,
}) => {
  // IAM-native: server-computed from actual IAM role membership (GET
  // /users/me), not the collapsed `role` string — a user can hold several
  // roles at once, and the old `role === RETRACED_ADMIN` compare only ever
  // saw one of them.
  const isRetracedAdmin = Boolean(me?.isRetracedAdmin);
  const isRetracedStaff = Boolean(me?.isRetracedStaff);
  const { VITE_RETRACED_ENV } = import.meta.env;
  const isStaging = [
    'staging',
    'team-product-compliance',
    'team-supply-chain-network-management',
    'demo',
  ].includes(VITE_RETRACED_ENV);
  const isProduction = VITE_RETRACED_ENV === 'production';

  return (
    <div
      id='sideMenu'
      className={`sidebar ${isNavbarCollapsed ? 'is-collapsed' : 'is-expanded'} ${className}`}
      style={{ borderRightWidth: 1, boxShadow: 'none' }}
    >
      <div className='sidebar-body'>
        <Menu isCollapsed={isNavbarCollapsed}>
          <MenuItem
            name='Impersonate'
            icon='fa-light fa-user-secret'
            path='/management/impersonate'
            highlightRegex={/^\/management\/impersonate/}
            disabled={
              (isProduction && !isRetracedAdmin) ||
              (isStaging && !isRetracedStaff)
            }
          />
          <hr />

          <MenuItem
            name='CS Tools'
            icon='fa-light fa-toolbox'
            path='/management/cs-tools'
            highlightRegex={/^\/management\/cs-tools/}
          />
          <MenuItem
            name='Users'
            icon='fa-light fa-user-friends'
            path='/management/users/list'
            highlightRegex={/^\/management\/users/}
          />
          <MenuItem
            name='Companies'
            icon='fa-light fa-building'
            path='/management/companies/list'
            highlightRegex={/^\/management\/companies\//}
          />
          <MenuItem
            name='Certifications'
            icon='fa-light fa-stamp'
            path='/management/claims/list?filter=%7B%22isArchived%22%3A%220%22%2C%22isRemoved%22%3A%220%22%7D&page=0&sort=updatedAt%3ADESC'
            highlightRegex={/^\/management\/claims/}
          />
          <MenuItem
            name='Email Bounce Tracking'
            icon='fa-light fa-envelope'
            path='/management/email-bounce-tracking/list'
            highlightRegex={/^\/management\/email-bounce-tracking/}
          />
          <MenuItem
            name='Permissions'
            icon='fa-light fa-shield-keyhole'
            path='/management/permissions'
            highlightRegex={/^\/management\/permissions/}
          />
          <hr />
          <MenuItem
            name='Documents'
            icon='fa-light fa-folder-open'
            path='/management/documents/list'
            highlightRegex={/^\/management\/documents/}
          />

          <MenuItem
            name='Document Types'
            icon='fa-light fa-file'
            path='/management/document-types/list'
            highlightRegex={/^\/management\/document-types/}
          />

          <MenuItem
            name='Audits'
            icon='fa-light fa-tag'
            path='/management/audits/list'
            highlightRegex={/^\/management\/audits(\/|$)/}
          />

          <MenuItem
            name='Audit Types'
            icon='fa-light fa-clipboard'
            path='/management/audit-types/list'
            highlightRegex={/^\/management\/audit-types(\/|$)/}
          />

          <MenuItem
            name='Risk Analysis'
            icon='fa-light fa-mountains'
            path='/management/risk-management'
            highlightRegex={/^\/management\/risk-management/}
          />

          <MenuItem
            name='Attachment Templates'
            icon='fa-light fa-books'
            path='/management/attachment-templates/list'
            highlightRegex={/^\/management\/attachment-templates/}
          />

          <MenuItem
            name='Question Templates'
            icon='fa-light fa-question'
            path='/management/question-templates'
            highlightRegex={/^\/management\/question-templates/}
          />

          <MenuItem
            name='Custom Information Fields'
            icon='fa-light fa-keyboard'
            path='/management/custom-information-fields/list'
            highlightRegex={/^\/management\/custom-information-fields/}
          />

          <MenuItem
            name='Production processes'
            icon='fa-light fa-briefcase'
            path='/management/production-processes/list'
            highlightRegex={/^\/management\/production-processes/}
          />

          <MenuItem
            name='Facility Processes'
            icon='fa-light fa-cogs'
            path='/management/facility-processes/list'
            highlightRegex={/^\/management\/facility-processes/}
          />

          <MenuItem
            name='Relationship Types'
            icon='fa-light fa-arrows-left-right-to-line'
            path='/management/relationship-types/list'
            highlightRegex={/^\/management\/relationship-types/}
          />

          <MenuItem
            name='Collaboration Statuses'
            icon='fa-light fa-timeline'
            path='/management/collaboration-statuses/list'
            highlightRegex={/^\/management\/collaboration-statuses/}
          />

          <MenuItem
            name='Questionnaire Templates'
            icon='fa-light fa-file-alt'
            path='/management/supplier-questionnaire-templates'
            highlightRegex={/^\/management\/supplier-questionnaire-templates/}
          />

          <MenuItem
            name='SLCP Dashboard'
            icon='fa-light fa-chart-line'
            path='/management/slcp-dashboard'
            highlightRegex={/^\/management\/slcp-dashboard/}
          />

          <hr />
          <MenuItem
            name='Webshops'
            icon='fa-light fa-store'
            path='/management/webshops/list'
            highlightRegex={/^\/management\/webshops/}
          />

          <MenuItem
            name='Webshop Origins'
            icon='fa-light fa-link'
            path='/management/origins/list'
            highlightRegex={/^\/management\/origins/}
          />
          <hr />

          <MenuItem
            name='Platform Features'
            icon='fa-light fa-clipboard-list-check'
            path='/management/platform-features/list'
            highlightRegex={/^\/management\/platform-features/}
          />

          <MenuItem
            name='Development tags'
            icon='fa-light fa-user-helmet-safety'
            path='/management/development-tags'
            highlightRegex={/^\/management\/development-tags/}
          />
          <MenuItem
            name='Tag Identifications'
            icon='fa-light fa-qrcode'
            path='/management/tag-identifications/list'
            highlightRegex={/^\/management\/tag-identifications/}
          />
          <hr />

          <MenuItem
            name='Production process templates'
            icon='fa-light fa-conveyor-belt'
            path='/management/fiber-production-process/list'
            highlightRegex={/^\/management\/fiber-production-process/}
          />

          <MenuItem
            name='Production factors'
            icon='fa-light fa-industry'
            path='/management/fiber-production-factors/list'
            highlightRegex={/^\/management\/fiber-production-factors/}
          />

          <MenuItem
            name='Fiber Coc Models'
            icon='fa-light fa-clipboard-list-check'
            path='/management/fiber-coc-models/list'
            highlightRegex={/^\/management\/fiber-coc-models/}
          />

          <MenuItem
            name='Fiber Settings'
            icon='fa-light fa-cogs'
            path='/management/fiber-settings'
            highlightRegex={/^\/management\/fiber-settings/}
          />

          <hr />

          <MenuItem
            name='Materials'
            icon='fa-light fa-seedling'
            path='/management/materials/list'
            highlightRegex={/^\/management\/materials/}
          />

          <MenuItem
            name='Product Categories'
            icon='fa-light fa-shapes'
            path='/management/product-categories/list'
            highlightRegex={/^\/management\/product-categories/}
          />

          <MenuItem
            name='Certifications'
            icon='fa-light fa-file-certificate'
            path='/management/standards/list'
            highlightRegex={/^\/management\/standards/}
          />

          <MenuItem
            name='Badges'
            icon='fa-light fa-award'
            path='/management/badges/list'
            highlightRegex={/^\/management\/badges/}
          />

          <hr />
          <MenuItem
            name='Data Fields'
            icon='fal fa-code'
            path='/management/classification-data-fields'
            highlightRegex={/^\/management\/classification-data-fields/}
          />
          <MenuItem
            name='Link Classifications'
            icon='fa-light fa-link'
            path='/management/classification-mapping'
            highlightRegex={/^\/management\/classification-mapping/}
          />
          <MenuItem
            name='Classification Views'
            icon='fa-light fa-code'
            path='/management/classification-views'
            highlightRegex={/^\/management\/classification-views/}
          />
          <hr />

          <MenuItem
            name='Files'
            icon='fa-light fa-file'
            path='/management/files/list'
            highlightRegex={/^\/management\/files/}
          />

          {isRetracedAdmin && (
            <MenuItem
              name='Rules'
              icon='fa-light fa-gavel'
              path='/management/rules/list'
              highlightRegex={/^\/management\/rules/}
            />
          )}
          <hr />
          <SideBarAccount {...accountProps} />
        </Menu>
      </div>

      <button
        type='button'
        className='no-styled-button sidebar-footer is-hidden-mobile'
        onClick={toggleNavbar}
      >
        <span className='icon is-medium'>
          <i
            className={`fa-2x fa-light fa-angle-double-${isNavbarCollapsed ? 'right' : 'left'}`}
            style={{ fontSize: '1.2rem' }}
          />
        </span>
      </button>
    </div>
  );
};

const MenuItem = ({
  name,
  icon,
  path,
  highlightRegex = '',
  onClick,
  disabled,
  rightComponent,
  className,
}) => {
  const { pathname } = useLocation();
  const isNavbarCollapsed = useNavbar(state => state.isNavbarCollapsed);
  const active =
    highlightRegex !== '' ? Boolean(pathname.match(highlightRegex)) : false;
  const { getTranslation } = useTranslator({ page: 'sidebar' });

  return isNavbarCollapsed ? (
    <Tooltip
      placement='right'
      trigger={_props => (
        <Link
          to={path}
          replace={path}
          className={disabled ? 'disabled-list-item' : ''}
        >
          {onClick ? (
            <UIMenuItem
              icon={icon}
              title={getTranslation(name)}
              data-cy={`side-menu-item-${name}`}
              active={active}
              iconStyle={{ paddingLeft: 5 }}
              onClick={onClick}
              className={classNames(
                className,
                disabled ? 'disabled-list-item' : '',
              )}
              rightComponent={rightComponent ? rightComponent : null}
            />
          ) : (
            <UIMenuItem
              icon={icon}
              title={getTranslation(`${name}`)}
              data-cy={`side-menu-item-${name}`}
              active={active}
              iconStyle={{ paddingLeft: 5 }}
              className={classNames(
                className,
                disabled ? 'disabled-list-item' : '',
              )}
              rightComponent={rightComponent ? rightComponent : null}
            />
          )}
        </Link>
      )}
    >
      <p className='help'>{name}</p>
    </Tooltip>
  ) : onClick ? (
    <UIMenuItem
      icon={icon}
      title={getTranslation(name)}
      data-cy={`side-menu-item-${name}`}
      onClick={onClick}
      rightComponent={rightComponent ? rightComponent : null}
      className={classNames(className, disabled ? 'disabled-list-item' : '')}
    />
  ) : (
    <Link className={disabled ? 'disabled-list-item' : ''} to={path}>
      <UIMenuItem
        icon={icon}
        title={getTranslation(name)}
        data-cy={`side-menu-item-${name}`}
        className={classNames(className, disabled ? 'disabled-list-item' : '')}
        active={active}
        rightComponent={rightComponent ? rightComponent : null}
      />
    </Link>
  );
};

const Sidebar = ({ className, accountProps }) => {
  const isNavbarCollapsed = useNavbar(state => state.isNavbarCollapsed);
  const { me, companyId, isImpersonating, isAdmin, isRetracedStaff } = useAuth(
    state => ({
      me: state.me,
      companyId: state.companyId,
      isImpersonating: state.isImpersonating,
      isAdmin: state.isAdmin,
      isRetracedStaff: state.isRetracedStaff,
    }),
  );

  const toggleNavbar = useNavbar(state => state.toggleNavbar);

  if (!me) return null;

  const isCompanylessUser = isCompanyless(me, { companyId });

  return isCompanylessUser ? (
    <CompanylessSidebar
      isNavbarCollapsed={isNavbarCollapsed}
      className={className}
      accountProps={accountProps}
      toggleNavbar={toggleNavbar}
    />
  ) : isAdmin() || isRetracedStaff() ? (
    <ManagementSidebar
      isNavbarCollapsed={isNavbarCollapsed}
      className={className}
      me={me}
      toggleNavbar={toggleNavbar}
      accountProps={accountProps}
    />
  ) : (
    <DashboardSidebar
      isNavbarCollapsed={isNavbarCollapsed}
      className={className}
      accountProps={accountProps}
      isImpersonating={isImpersonating}
      toggleNavbar={toggleNavbar}
    />
  );
};

export default Sidebar;
